import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import { useBrand } from '../contexts/BrandContext';
import { BrandName } from '../components/Common/BrandedText';
import { supabase } from '../utils/supabaseClient';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  CogIcon,
  CurrencyDollarIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

// Import step components
import CompanyBasicsStep from './Onboarding/CompanyBasicsStep';
import ServicesStep from './Onboarding/ServicesStep';
import PricingSetupStep from './Onboarding/PricingSetupStep';
import TeamSetupStep from './Onboarding/TeamSetupStep';
import BusinessPreferencesStep from './Onboarding/BusinessPreferencesStep';
import FinancialSetupStep from './Onboarding/FinancialSetupStep';
import GoLiveStep from './Onboarding/GoLiveStep';
import { detectBusinessTimezone } from '../utils/timezones';

// Compare two onboarding_progress blobs by content: key order differs between a jsonb value
// read back from the database and an object spread together here, and last_auto_save is a
// stamp, not progress.
const progressKey = (p) => {
  if (!p || typeof p !== 'object') return JSON.stringify(p ?? null);
  const stable = (v) => {
    if (Array.isArray(v)) return v.map(stable);
    if (v && typeof v === 'object') {
      return Object.keys(v).sort().reduce((o, k) => { o[k] = stable(v[k]); return o; }, {});
    }
    return v;
  };
  const { last_auto_save, ...rest } = p;
  return JSON.stringify(stable(rest));
};

const OnboardingWizard = ({ onComplete, onSkip, onChangeMode, quickStartMode: propQuickStartMode }) => {
  const { user } = useUser();
  const { brand } = useBrand();
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress] = useState(null);
  // The progress blob as this tab last saw it IN THE DATABASE (loaded, returned by the RPC, or
  // written by the auto-save). The auto-save writes only when `progress` differs from it.
  const savedProgressRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState({ valid: true, errors: [], warnings: [] });
  // The auto-save indicator was a green dot that pulsed "Auto-saving progress" forever,
  // bound to nothing. It said the same thing while idle, while saving, and -- because the
  // write error is caught and logged as "Auto-save failed (this is OK)" -- immediately
  // after a save had failed. saveState drives it from what actually happened.
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | failed
  const [quickStartMode, setQuickStartMode] = useState(() => {
    // Use prop first, then localStorage, then default to false
    if (propQuickStartMode !== undefined) {
      localStorage.setItem('onboarding_quickStartMode', propQuickStartMode.toString());
      return propQuickStartMode;
    }
    const saved = localStorage.getItem('onboarding_quickStartMode');
    return saved === 'true';
  });

  // Helper function to get step names
  const getStepName = (stepNumber) => {
    const stepNames = {
      1: 'Company Basics',
      2: 'Services & Pricing',
      3: 'Team Setup',
      4: 'Business Preferences',
      5: 'Financial Setup',
      6: 'Go Live'
    };
    return stepNames[stepNumber] || `Step ${stepNumber}`;
  }; // New: Quick Start mode

  // Step configuration - Dynamic based on mode
  const getSteps = () => [
    {
      id: 1,
      name: 'Company Basics',
      description: 'Set up your company profile',
      icon: BuildingOfficeIcon,
      component: CompanyBasicsStep,
      color: 'blue'
    },
    {
      id: 2,
      name: quickStartMode ? 'Pricing Setup' : 'Services & Pricing',
      description: quickStartMode ? 'Set your rates to start quoting' : 'Define what services you provide',
      icon: quickStartMode ? CurrencyDollarIcon : WrenchScrewdriverIcon,
      component: quickStartMode ? PricingSetupStep : ServicesStep,
      color: 'green'
    },
    {
      id: 3,
      name: 'Team Setup',
      description: 'Add your team members',
      icon: UserGroupIcon,
      component: TeamSetupStep,
      color: 'purple'
    },
    {
      id: 4,
      name: 'Business Preferences',
      description: 'Configure your business settings',
      icon: CogIcon,
      component: BusinessPreferencesStep,
      color: 'orange'
    },
    {
      id: 5,
      name: 'Financial Setup',
      description: 'Set up billing and payments',
      icon: CurrencyDollarIcon,
      component: FinancialSetupStep,
      color: 'indigo'
    },
    {
      id: 6,
      name: 'Go Live',
      description: 'Create your first quote and go live',
      icon: RocketLaunchIcon,
      component: GoLiveStep,
      color: 'pink'
    }
  ];

  const steps = getSteps();

  // The real ordered list of step ids the user actually visits.
  // Quick Start jumps 1 → 2 → 6 (skips 3,4,5), so it's a 3-step flow.
  const flowStepIds = quickStartMode ? [1, 2, 6] : steps.map(s => s.id);

  // WHERE THE USER ACTUALLY WAS, not where they last FINISHED.
  //
  // onboarding_progress.current_step only moves when a step COMPLETES:
  // update_onboarding_progress advances it inside `IF p_completed THEN` and does nothing
  // to it otherwise. So arriving at a step records nothing, and anything that leaves the
  // app mid-step brings the user back to the last step they finished -- which for a fresh
  // company is step 1, the very beginning.
  //
  // Stripe Connect is where this bites: the contractor starts Connect onboarding from the
  // Go Live step, completes it at Stripe, and Stripe returns them to the app. They land on
  // the first page of the wizard having just done the hardest part of setup.
  //
  // Remembered in localStorage rather than by making the RPC write current_step on arrival:
  // calling update_onboarding_progress with p_completed=false would also flip
  // steps[N].completed to FALSE, un-completing a step merely by visiting it. This also
  // covers every other way out of the flow -- a refresh, a closed tab, an email link -- not
  // just the Stripe round trip.
  const resumeKey = user?.company_id ? `onboarding_step_${user.company_id}` : null;
  useEffect(() => {
    // Not while still loading: the initial render sits on step 1 before progress arrives,
    // and persisting that would overwrite the very value being restored.
    if (loading || !resumeKey) return;
    localStorage.setItem(resumeKey, String(currentStep));
  }, [currentStep, loading, resumeKey]);


  // 1-based position of the current step within the active flow (0 if not found).
  const currentFlowIndex = flowStepIds.indexOf(currentStep) + 1;

  // No percentage is computed any more. The stepper and the header's "Step X of Y" both
  // report position, and the bar that showed a percentage has been removed as the third
  // copy of the same fact.

  // Load onboarding progress
  useEffect(() => {
    loadProgress();
  }, [user?.company_id]);

  const loadProgress = async () => {
    if (!user?.company_id) return;

    try {
      setLoading(true);

      // FIXED: Handle missing onboarding_progress column gracefully
      const { data, error } = await supabase
        .from('company_settings')
        .select('onboarding_progress')
        .eq('company_id', user.company_id)
        .single();

      let progressData;

      if (error) {
        if (error.code === 'PGRST116') {
          // No company_settings record exists - create one
          console.log('📝 Creating company_settings record for onboarding...');
          const { error: insertError } = await supabase
            .from('company_settings')
            .insert({
              company_id: user.company_id,
              onboarding_progress: {
                current_step: 1,
                completed_steps: [],
                started_at: new Date().toISOString(),
                completed_at: null,
                skipped: false,
                steps: {
                  "1": {"name": "Company Basics", "completed": false, "completed_at": null},
                  "2": {"name": "Services & Pricing", "completed": false, "completed_at": null},
                  "3": {"name": "Team Setup", "completed": false, "completed_at": null},
                  "4": {"name": "Business Preferences", "completed": false, "completed_at": null},
                  "5": {"name": "Financial Setup", "completed": false, "completed_at": null},
                  "6": {"name": "Go Live", "completed": false, "completed_at": null}
                }
              }
            });

          if (insertError && !insertError.message.includes('onboarding_progress')) {
            console.error('Error creating company_settings:', insertError);
          }

          progressData = {
            current_step: 1,
            completed_steps: [],
            started_at: new Date().toISOString(),
            completed_at: null,
            skipped: false,
            steps: {}
          };
        } else if (error.message.includes('onboarding_progress') || error.code === '42703') {
          // Column doesn't exist - use default progress
          console.log('⚠️ onboarding_progress column missing - using default progress');
          progressData = {
            current_step: 1,
            completed_steps: [],
            started_at: new Date().toISOString(),
            completed_at: null,
            skipped: false,
            steps: {}
          };
        } else {
          throw error;
        }
      } else {
        progressData = data?.onboarding_progress || {
          current_step: 1,
          completed_steps: [],
          started_at: new Date().toISOString(),
          completed_at: null,
          skipped: false,
          steps: {}
        };
      }

      setProgress(progressData);
      savedProgressRef.current = progressKey(progressData);
      const savedStep = resumeKey ? parseInt(localStorage.getItem(resumeKey), 10) : NaN;
      // CLAMP INTO THE ACTIVE FLOW. current_step is stored per company and counts the FULL
      // six-step flow, but Quick Start only runs [1, 2, 6]. A company that had reached step
      // 3 (Team) and then chose Quick Start was dropped straight onto Team Members -- a
      // screen Quick Start is supposed to skip -- and the progress bar read 0%, because
      // indexOf(3) in [1,2,6] is -1. Same defect, two symptoms.
      const clampToFlow = (step) => {
        if (flowStepIds.includes(step)) return step;
        // Nearest step in this flow at or after the stored one, so progress is never lost;
        // past the end means the flow is done, so sit on its final step.
        return flowStepIds.find((id) => id >= step) ?? flowStepIds[flowStepIds.length - 1];
      };
      const dbStep = clampToFlow(progressData.current_step || 1);

      // Board #2104: the remembered step is not scoped to the flow it was recorded in, and
      // switching flows changes what a step id MEANS. Finish Quick Start's step 2 and you sit
      // on Go Live, so the resume key holds 6 = "last step of Quick [1,2,6]". Choose "Do the
      // full setup instead" and the flow becomes [1..6], where 6 is the last of SIX - and the
      // old test (in this flow, and not behind dbStep=3) accepted it, opening the wizard on
      // "Step 6 of 6 - Go Live". The three steps the user had just asked for were reachable
      // only by pressing Back, and the prominent Continue would have finished onboarding
      // without ever showing them.
      //
      // So also require that everything BEFORE the remembered step is actually done. That
      // keeps the case this resume exists for - returning from Stripe Connect onto Go Live,
      // where the earlier steps ARE complete - and rejects a step id inherited from a shorter
      // flow.
      const stepsDone = progressData.steps || {};
      const priorFlowStepsComplete = flowStepIds
        .filter((id) => id < savedStep)
        .every((id) => stepsDone[String(id)]?.completed);

      // Only honour a remembered step that is part of THIS flow (quick start shows 1, 2, 6)
      // and is not behind what the database already considers done.
      setCurrentStep(
        Number.isFinite(savedStep)
          && flowStepIds.includes(savedStep)
          && savedStep >= dbStep
          && priorFlowStepsComplete
          ? savedStep
          : dbStep
      );


      // If already completed, call onComplete
      if (progressData.completed_at) {
        // Finished: drop the resume marker so a later visit cannot be pulled back into it.
        if (resumeKey) localStorage.removeItem(resumeKey);
        onComplete?.();
        return;
      }

    } catch (error) {
      console.error('Error loading onboarding progress:', error);
      // Fallback to default progress
      const defaultProgress = {
        current_step: 1,
        completed_steps: [],
        started_at: new Date().toISOString(),
        completed_at: null,
        skipped: false,
        steps: {}
      };
      setProgress(defaultProgress);
      setCurrentStep(1);
    } finally {
      setLoading(false);
    }
  };

  // Validate current step
  const validateStep = async (stepNumber) => {
    if (!user?.company_id) return { valid: false, errors: ['No company ID'], warnings: [] };

    try {
      const { data, error } = await supabase.rpc('validate_onboarding_step', {
        p_company_id: user.company_id,
        p_step: stepNumber
      });

      if (error) throw error;
      return data || { valid: true, errors: [], warnings: [] };
    } catch (error) {
      if (error.message.includes('validate_onboarding_step') || error.code === '42883') {
        console.log('⚠️ Onboarding validation functions missing - using basic validation');
        // Basic validation - just check if we have required data
        return { valid: true, errors: [], warnings: [] };
      }
      console.error('Error validating step:', error);
      return { valid: false, errors: ['Validation failed'], warnings: [] };
    }
  };

  // Update progress in database
  const updateProgress = async (stepNumber, completed = true) => {
    if (!user?.company_id) return;

    try {
      setSaving(true);

      // FIXED: Handle missing onboarding functions gracefully
      try {
        const { data, error } = await supabase.rpc('update_onboarding_progress', {
          p_company_id: user.company_id,
          p_step: stepNumber,
          p_completed: completed
        });

        if (error) throw error;

        setProgress(data);
        savedProgressRef.current = progressKey(data);
        return data;
      } catch (rpcError) {
        if (rpcError.message.includes('update_onboarding_progress') || rpcError.code === '42883') {
          console.log('⚠️ Onboarding RPC functions missing - using local progress tracking');

          // Fallback: Update progress locally
          const updatedProgress = {
            ...progress,
            current_step: completed && stepNumber < 6 ? stepNumber + 1 : stepNumber,
            completed_steps: completed ? [...(progress.completed_steps || []), stepNumber] : progress.completed_steps,
            steps: {
              ...progress.steps,
              [stepNumber]: {
                name: getStepName(stepNumber),
                completed: completed,
                completed_at: completed ? new Date().toISOString() : null
              }
            },
            last_updated: new Date().toISOString()
          };

          setProgress(updatedProgress);
          return updatedProgress;
        } else {
          throw rpcError;
        }
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Auto-save progress every 30 seconds - Addressing "never lose progress"
  //
  // Only when something changed, and never over newer progress (investigation
  // 2026-10-25-writes-on-page-view.md item 8). It used to rewrite company_settings every 30 s
  // whether or not anything had changed, and a second tab (or a step that saves into the blob
  // itself, e.g. AssessmentStep's `assessment`) was overwritten by this tab's older copy on its
  // next tick. Now: nothing changed since the database last had it -> no write. Changed -> read
  // the stored blob first; if another writer moved it, take theirs instead of overwriting it;
  // otherwise write, conditional on updated_at not having moved since that read.
  useEffect(() => {
    if (!user?.company_id || !progress) return;

    const autoSaveInterval = setInterval(async () => {
      try {
        if (progressKey(progress) === savedProgressRef.current) return; // nothing to save

        const { data: current, error: readError } = await supabase
          .from('company_settings')
          .select('onboarding_progress, updated_at')
          .eq('company_id', user.company_id)
          .maybeSingle();
        if (readError || !current) {
          if (readError) console.warn('⚠️ Auto-save failed (this is OK):', readError.message);
          return;
        }
        if (progressKey(current.onboarding_progress) !== savedProgressRef.current) {
          // Someone else saved newer progress since this tab last saw it: never overwrite it.
          savedProgressRef.current = progressKey(current.onboarding_progress);
          if (current.onboarding_progress) setProgress(current.onboarding_progress);
          return;
        }

        const next = { ...progress, last_auto_save: new Date().toISOString() };
        const { data: written, error } = await supabase
          .from('company_settings')
          .update({
            onboarding_progress: next,
            updated_at: new Date().toISOString()
          })
          .eq('company_id', user.company_id)
          .eq('updated_at', current.updated_at)
          .select('company_id');

        if (error) {
          console.warn('⚠️ Auto-save failed (this is OK):', error.message);
        } else if (written && written.length > 0) {
          savedProgressRef.current = progressKey(next);
        }
        // 0 rows: the row changed between the read and the write; the next tick re-checks.
      } catch (error) {
        console.warn('⚠️ Auto-save error (this is OK):', error);
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [user?.company_id, progress]);

  // Handle step completion
  const handleStepComplete = async (stepData) => {
    console.log('🔄 OnboardingWizard handleStepComplete called for step:', currentStep);

    setSaving(true);
    setSaveState('saving');
    try {
      // CRITICAL FIX: Use UPDATE instead of UPSERT to prevent duplicate records
      if (stepData && user?.company_id) {
        console.log('💾 Saving step data to database...');
        const { error: updateError } = await supabase
          .from('company_settings')
          .update({
            onboarding_progress: {
              ...progress,
              [`step_${currentStep}_data`]: stepData,
              last_auto_save: new Date().toISOString()
            },
            updated_at: new Date().toISOString()
          })
          .eq('company_id', user.company_id);

        if (updateError) {
          // Still not fatal -- the step's own save owns the real data, this is the
          // progress blob. But it is no longer invisible: the indicator says so.
          setSaveState('failed');
          console.warn('⚠️ Auto-save failed (this is OK):', updateError.message);
        } else {
          console.log('✅ Step data saved successfully');
        }
      }

      // Validate step
      console.log('🔍 Validating step...');
      const validation = await validateStep(currentStep);
      console.log('📊 Validation result:', validation);
      setValidation(validation);

      if (!validation.valid) {
        console.log('❌ Validation failed, stopping here');
        console.log('❌ Validation errors:', validation.errors);
        return; // Don't proceed if validation fails
      }

      // Update progress
      console.log('📈 Updating progress...');
      const newProgress = await updateProgress(currentStep, true);
      console.log('✅ Progress updated:', newProgress);

      // Quick Start mode: skip to final step after step 2
      if (quickStartMode && currentStep === 2) {
        console.log('🚀 Quick Start mode: jumping to Go Live step');
        setCurrentStep(6); // Jump to Go Live step
      } else if (flowStepIds.indexOf(currentStep) >= 0
                 && flowStepIds.indexOf(currentStep) < flowStepIds.length - 1) {
        // Next step IN THE FLOW, for the same reason goBack uses it: currentStep + 1 is
        // only correct when the flow happens to be contiguous.
        //
        // The >= 0 guard is the point. Completing a step that is NOT in the active flow
        // gave indexOf === -1, which passed the old `-1 < length - 1` test and then read
        // flowStepIds[0] -- sending the user to STEP ONE. Reported live: finishing Team
        // Members inside Quick Start bounced back to Company Basics. The restore is now
        // clamped so an out-of-flow step should not be reachable, but a -1 that silently
        // means "the first step" is the kind of arithmetic that comes back.
        const nextStep = flowStepIds[flowStepIds.indexOf(currentStep) + 1];
        console.log('➡️ Moving to next step:', nextStep);
        setCurrentStep(nextStep);
      } else if (flowStepIds.indexOf(currentStep) < 0) {
        // Finished a step outside this flow: rejoin at the nearest step the flow still
        // expects, rather than declaring setup complete on the strength of a step this
        // flow never asked for.
        const rejoin = flowStepIds.find((id) => id > currentStep) ?? flowStepIds[flowStepIds.length - 1];
        setCurrentStep(rejoin);
      } else {
        console.log('🎉 Onboarding complete!');
        // Onboarding complete
        onComplete?.(newProgress);
      }

    } catch (error) {
      console.error('❌ Error completing step:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle navigation
  const goToStep = (stepNumber) => {
    if (stepNumber >= 1 && stepNumber <= 6) {
      setCurrentStep(stepNumber);
    }
  };

  // BACK MOVES WITHIN THE ACTIVE FLOW, not by decrementing the raw step id.
  //
  // Quick Start is steps [1, 2, 6] -- forward from 2 jumps straight to 6. Back was a bare
  // currentStep - 1, so from the Quick path's last step it landed on 5 (Financial Setup),
  // a step that user never opted into. The counter then had nothing to show: currentStep 5
  // is not in flowStepIds, so currentFlowIndex is 0 and the header fell through to the raw
  // id and rendered "Step 5 of 3". Pressing Back again walked into 4 and 3 -- Business
  // Preferences and Team Setup -- the whole Advanced flow, reachable only backwards.
  const goBack = () => {
    const position = flowStepIds.indexOf(currentStep);
    if (position > 0) {
      setCurrentStep(flowStepIds[position - 1]);
      return;
    }
    // Not in the flow at all (a resumed session whose saved step predates a mode switch).
    // Fall back to the last flow step BEFORE this one rather than to step 1, so resuming
    // does not silently restart the wizard.
    const previous = [...flowStepIds].reverse().find(id => id < currentStep);
    if (previous !== undefined) setCurrentStep(previous);
  };

  // The footer "Continue" button must trigger the CURRENT step's own save before
  // advancing. Steps that persist via an in-form Save button (Business Preferences,
  // Financial Setup) call onComplete only from that button — so clicking the more
  // prominent footer Continue used to advance WITHOUT saving, silently dropping the
  // form data. Each such step registers its save here; goNext runs it (which itself
  // calls onComplete → handleStepComplete with the data). Cleared on step change so a
  // stale handler from the previous step can't fire.
  // THE HANDLER IS TAGGED WITH ITS STEP, instead of being cleared by an effect.
  //
  // The clearing effect was `useEffect(() => { stepSaveRef.current = null }, [currentStep])`,
  // meant to stop a stale handler from the PREVIOUS step firing. React runs child effects
  // BEFORE parent effects, so on arriving at a step the real order was: the step registers
  // its save, then this effect nulled it. The ref was empty until some unrelated re-render
  // re-registered it -- the async company load, or the user's first keystroke, because
  // handleSave is a plain function with a new identity every render.
  //
  // In practice a filled form saved, because filling it IS the re-render that re-registers.
  // An untouched step fell through to handleStepComplete instead, which does not run the
  // step's own save. That is a data-loss bug held off by a coincidence, and it is exactly
  // the shape of "the footer button behaves differently from Save & Continue" that testers
  // kept reporting without being able to pin down.
  //
  // Tagging removes the ordering question entirely: a handler is used only if it was
  // registered by the step currently on screen, whatever order the effects ran in.
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;
  const stepSaveRef = useRef(null);
  const registerStepSave = useCallback((fn) => {
    stepSaveRef.current = fn ? { step: currentStepRef.current, fn } : null;
  }, []);

  const goNext = async () => {
    const registered = stepSaveRef.current;
    if (registered && registered.step === currentStep) {
      await registered.fn();
    } else {
      await handleStepComplete();
    }
  };

  // Skip onboarding. This runs only once the themed dialog has been confirmed -- it used
  // to open window.confirm itself, an OS dialog dropped into a dark wizard, which reads as
  // the page having fallen out of the app.
  const [skipPromptOpen, setSkipPromptOpen] = useState(false);

  useEffect(() => {
    if (!skipPromptOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setSkipPromptOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [skipPromptOpen]);

  const handleSkip = async () => {
    setSkipPromptOpen(false);
    // Skipping is a deliberate exit; the remembered step must not survive it.
    if (resumeKey) localStorage.removeItem(resumeKey);
    {
      try {
        // Skipping never shows the timezone field, so a company that never chose one stayed on
        // the column default (Los Angeles) for good. Same rule as CompanyBasicsStep: no saved
        // choice and still the default -> this device's zone. trg_mirror_company_timezone
        // copies it onto companies. Business Settings can change it later.
        const { data: tzRow } = await supabase
          .from('company_settings')
          .select('timezone')
          .eq('company_id', user.company_id)
          .maybeSingle();
        const detectedTz = !progress?.step_1_data?.timezone && tzRow?.timezone === 'America/Los_Angeles'
          ? detectBusinessTimezone()
          : null;

        // Mark as skipped
        await supabase
          .from('company_settings')
          .update({
            ...(detectedTz ? { timezone: detectedTz } : {}),
            onboarding_progress: {
              ...progress,
              skipped: true,
              skipped_at: new Date().toISOString()
            }
          })
          .eq('company_id', user.company_id);

        onSkip?.();
      } catch (error) {
        console.error('Error skipping onboarding:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading onboarding...</p>
        </div>
      </div>
    );
  }

  const currentStepConfig = steps.find(s => s.id === currentStep);
  const StepComponent = currentStepConfig?.component;

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900">
      {/* #4993: BusinessPreferencesStep applies the chosen theme to <html>, but this shell was
          hardcoded bg-slate-900 / text-white, so picking Light changed nothing the user could
          see and the selector read as broken. Every class below is a light/dark pair; the dark
          half is what it looked like before. */}
      {/* Header */}
      <div className="bg-white dark:bg-white/10 backdrop-blur-xl shadow-sm border-b border-slate-200 dark:border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white"><BrandName /> Setup</h1>
              <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                Step {currentFlowIndex > 0 ? currentFlowIndex : currentStep} of {flowStepIds.length}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <a
                href={`mailto:${brand.supportEmail}?subject=${encodeURIComponent(`${brand.name} setup help`)}`}
                className="text-blue-400 hover:text-blue-300 flex items-center gap-1 text-sm font-medium"
              >
                ✉ Need Help? Email Support
              </a>
              <button
                onClick={() => setSkipPromptOpen(true)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1"
              >
                <XMarkIcon className="w-4 h-4" />
                Skip Setup
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar - Enhanced with clear required/optional indicators */}
      <div className="bg-white dark:bg-white/10 backdrop-blur-xl border-b border-slate-200 dark:border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Time estimate and completion status */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/20 rounded-full text-sm text-blue-300">
              Guided Setup • Your progress is saved as you go
            </div>
          </div>

          <div className="flex items-center justify-between">
            {/* Only render steps in the active flow so Quick Start (1,2,6) doesn't
                show — or mark as past/complete — steps the user never visits. */}
            {steps.filter(s => flowStepIds.includes(s.id)).map((step, index, flowSteps) => {
              const isCompleted = progress?.steps?.[step.id]?.completed;
              const isCurrent = step.id === currentStep;
              // "Past" is based on position in the active flow, not raw id order.
              const isPast = flowStepIds.indexOf(step.id) < flowStepIds.indexOf(currentStep);
              // Step 1 only. Step 2 offers its own "Skip - I'll Quote Jobs Individually"
              // button, so labelling it Required contradicted a control on the step itself.
              const isRequired = step.id === 1;

              return (
                <div key={step.id} className="flex items-center">
                  <div className="text-center">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full border-2 cursor-pointer transition-colors ${
                        isCompleted
                          ? 'bg-green-500 border-green-500 text-white'
                          : isCurrent
                          ? `bg-${step.color}-500 border-${step.color}-500 text-white`
                          : isPast
                          ? 'bg-gray-300 border-gray-300 text-gray-600'
                          : 'bg-slate-100 border-slate-300 text-gray-500 dark:bg-white/10 dark:border-white/20 dark:text-gray-400'
                      }`}
                      onClick={() => goToStep(step.id)}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon className="w-6 h-6" />
                      ) : (
                        <step.icon className="w-5 h-5" />
                      )}
                    </div>
                    {/* Required/Optional indicator */}
                    <div className={`text-xs mt-1 ${isRequired ? 'text-red-400 font-medium' : 'text-gray-500'}`}>
                      {isRequired ? 'Required' : 'Optional'}
                    </div>
                  </div>

                  {index < flowSteps.length - 1 && (
                    <div className={`w-12 h-0.5 mx-2 ${
                      isPast ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 text-center">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">{currentStepConfig?.name}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">{currentStepConfig?.description}</p>
            {/* Clear indication of what's required vs optional */}
            {/* This badge describes the STEP, not its fields. It used to read "Required for
                basic functionality", which testers reasonably read as "every field here is
                required" -- and then found industry, address and profile description all
                saving empty. Only company name is required on step 1; the rest genuinely is
                optional. The badge's real meaning is that these two steps have no Skip
                button (see the Skip control below, gated on currentStep > 2), so it says
                that instead. */}
            {currentStep === 1 ? (
              <div className="mt-2 inline-flex items-center gap-1 text-sm text-red-400">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                This step can't be skipped — only fields marked * are required
              </div>
            ) : currentStep === 2 ? (
              /* Step 2 has its own skip button, so it is neither "can't be skipped" nor
                 simply optional — say what the button on the step actually does. */
              <div className="mt-2 inline-flex items-center gap-1 text-sm text-amber-400">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                Set up pricing now, or skip and quote jobs individually
              </div>
            ) : (
              <div className="mt-2 inline-flex items-center gap-1 text-sm text-gray-500">
                <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                Optional - can be completed later
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Validation Messages */}
      {validation.errors.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-4 mt-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Please fix these issues:</h3>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                {validation.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {validation.warnings.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mx-4 mt-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Recommendations:</h3>
              <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                {validation.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Mode selection is now handled by OnboardingWelcome component */}

      {/* Step Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {StepComponent && (
          <StepComponent
            onComplete={handleStepComplete}
            onValidationChange={setValidation}
            registerSave={registerStepSave}
            progress={progress}
            quickStartMode={quickStartMode}
          />
        )}
      </div>

      {/* Navigation - Enhanced with better progress indicators */}
      <div className="bg-white dark:bg-white/10 backdrop-blur-xl border-t border-slate-200 dark:border-white/20 px-4 py-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* PROGRESS LIVES AT THE TOP; THIS BAR IS CONTROLS. It was being reported three
              times over: "Step X of Y" in the header, the stepper with its Required /
              Optional circles, and a "Setup Progress — N% Complete" bar down here, wrapped
              around the Back and Continue buttons. Three readings of one fact, and the
              third one mixed a status display into the navigation.

              The stepper is the better indicator -- it names the steps and marks which are
              optional -- so the duplicate bar is gone and this band does one job.

              (The percentage it showed was corrected in the previous commit to count
              completed steps rather than position. Removing the bar makes that moot, which
              is the right outcome: a number nobody needed to read was being computed
              wrongly and then displayed twice over.) */}
          <div className="mb-4">
            {quickStartMode && (
              <div className="mt-2 text-xs text-blue-400 text-center">
                ⚡ Quick Start Mode - Only essential steps<br/>
                <span className="text-gray-500">You can complete Team, Business & Financial setup later in Settings</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={goBack}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-slate-100 dark:bg-white/10 border rounded-lg border-slate-300 dark:border-slate-700/40 hover:bg-slate-200 dark:hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>

            {/* Help and status indicators */}
            <div className="flex items-center gap-4">

              {/* The chosen flow now SURVIVES leaving setup (see ProtectedRoute), which is
                  what Continue Setup promises — but a choice you cannot revisit is its own
                  trap, so changing your mind gets a control instead of a cleared key. */}
              {onChangeMode && (
                <button
                  type="button"
                  onClick={onChangeMode}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline decoration-dotted underline-offset-2"
                >
                  Switch setup mode
                </button>
              )}

              {/* Auto-save indicator — reflects the last actual write, not a mood. */}
              {saveState !== 'idle' && (
                <div className={`text-xs flex items-center gap-1 ${
                  saveState === 'failed' ? 'text-amber-400' : 'text-gray-500'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    saveState === 'saving' ? 'bg-green-500 animate-pulse'
                      : saveState === 'saved' ? 'bg-green-500'
                      : 'bg-amber-500'
                  }`}></div>
                  {saveState === 'saving' ? 'Saving progress…'
                    : saveState === 'saved' ? 'Progress saved'
                    : 'Progress not saved — your entries are still on screen'}
                </div>
              )}

            </div>

            <div className="flex items-center gap-2">
              {/* Skip current step if optional.
                  The final step is excluded because there is nothing after it to skip TO.
                  The render guard was `currentStep > 2 && !quickStartMode` while the handler
                  below only acts when `position < flowStepIds.length - 1`, so on the Go Live
                  step the button drew itself and then did nothing — sitting next to Complete
                  Setup, which made the finish action ambiguous as well as inert. Same test the
                  Complete Setup label uses, so the two cannot drift apart again. */}
              {currentStep > 2 && !quickStartMode
                && currentStep !== flowStepIds[flowStepIds.length - 1] && (
                <button
                  onClick={() => {
                    const position = flowStepIds.indexOf(currentStep);
                    if (position > -1 && position < flowStepIds.length - 1) {
                      setCurrentStep(flowStepIds[position + 1]);
                    }
                  }}
                  className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  Skip This Step
                </button>
              )}

              <button
                onClick={goNext}
                disabled={saving || (!validation.valid && currentStep <= 2)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : currentStep === flowStepIds[flowStepIds.length - 1] ? (
                  // Only the LAST step of the ACTIVE flow completes. This used to read
                  // `currentStep === 6 || (quickStartMode && currentStep === 2)`, so Quick
                  // Start step 2 was labelled "Complete Setup" while handleStepComplete
                  // jumped to step 6 (Go Live). The button said it was finishing and then
                  // carried on -- pressing Complete Setup continued.
                  <>
                    Complete Setup
                    <CheckCircleIcon className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRightIcon className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Themed skip confirmation, replacing window.confirm. Escape and the backdrop both
          cancel, which is what a native confirm gave for free and a hand-rolled one usually
          forgets. */}
      {skipPromptOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setSkipPromptOpen(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="skip-setup-title"
            className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-2xl"
          >
            <h3 id="skip-setup-title" className="text-lg font-semibold text-gray-900 dark:text-white">Skip setup for now?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              You can finish this any time from Settings. Nothing you have already entered is lost.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSkipPromptOpen(false)}
                className="px-4 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Keep setting up
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                Skip setup
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default OnboardingWizard;

